import {
  aadhaarDocumnetId,
  corrAadhaarDocumnetId,
} from "../../utils/constants";

// Pairing rules for two-sided address proofs (Aadhaar front/back) on the
// Personal Documents page. Each card owns two fixed slots: the first slot is
// always the front and the second the back, and a slot only ever pairs with
// the other slot of the SAME card (permanent or correspondence).

export type AddressSlotId =
  | "permAddress"
  | "permSecondAddress"
  | "corrAddress"
  | "corrSecondAddress";

export interface SideDoc {
  base64?: string;
  fileName?: string;
  documentcategory?: string;
  documentId?: number;
}

export const isCorrSlot = (slotId: string): boolean =>
  slotId === "corrAddress" || slotId === "corrSecondAddress";

export const isBackSlot = (slotId: string): boolean =>
  slotId === "permSecondAddress" || slotId === "corrSecondAddress";

export const frontSlotOf = (slotId: string): AddressSlotId =>
  isCorrSlot(slotId) ? "corrAddress" : "permAddress";

export const backSlotOf = (slotId: string): AddressSlotId =>
  isCorrSlot(slotId) ? "corrSecondAddress" : "permSecondAddress";

export const isAadhaarDocId = (docId?: number | string): boolean =>
  docId !== undefined &&
  docId !== null &&
  docId !== "" &&
  (+docId === aadhaarDocumnetId || +docId === corrAadhaarDocumnetId);

/**
 * storeOcrData routes Aadhaar OCR by document id (117 = permanent,
 * 10 = correspondence), so the id must follow the card, not the dropdown.
 */
export const aadhaarOcrDocId = (slotId: string): number =>
  isCorrSlot(slotId) ? corrAadhaarDocumnetId : aadhaarDocumnetId;

/** The card a slot belongs to; OCR after masking must read this card only. */
export function getCardFront<T extends SideDoc>(
  slotId: string,
  permFront: T,
  corrFront: T
): T {
  return isCorrSlot(slotId) ? corrFront : permFront;
}

/** Both sides of one card hold content, so they must be merged together. */
export const hasBothSides = (front: SideDoc, back: SideDoc): boolean =>
  !!front?.base64 && !!back?.base64;

/** Merge payload images, always front first then back. */
export function getMergeImages(front: SideDoc, back: SideDoc) {
  return {
    image1: front.base64,
    imagename1: front.fileName,
    image2: back.base64,
    imagename2: back.fileName,
  };
}

/** Same file picked for both sides of a card. */
export const isSameImage = (a?: string, b?: string): boolean =>
  !!a && !!b && a === b;

export const isPdfFile = (fileName = ""): boolean => /\.pdf$/i.test(fileName);

/** Aadhaar numbers are only ever saved as their last 4 digits. */
export const documentNumberForSave = (
  selectedDocType: string,
  documentNumber = ""
): string =>
  selectedDocType === "Aadhaar Card"
    ? documentNumber.slice(-4)
    : documentNumber;
